<template>
    <div class="base-tree">
        <div
            v-if="$slots.label"
            class="tree-label"
        >
            <slot name="label" />
        </div>

        <el-tree
            ref="elTreeRef"
            :data="data"
            :props="treeProps"
            :node-key="nodeKey"
            :highlight-current="highlightCurrent"
            :accordion="accordion"
            :show-checkbox="showCheckbox"
            :check-strictly="checkStrictly"
            :default-checked-keys="defaultCheckedKeys"
            :default-expanded-keys="defaultExpandedKeys"
            :current-node-key="currentNodeKey"
            :lazy="lazy"
            :load="load"
            :draggable="draggable"
            :indent="indent"
            :icon-class="iconClass"
            :empty-text="emptyText"
            v-bind="$attrs"
            @node-click="handleNodeClick"
            @check-change="handleCheckChange"
            @current-change="handleCurrentChange"
            @check="handleCheck"
            @node-expand="handleNodeExpand"
            @node-collapse="handleNodeCollapse"
        >
            <template #default="{ node, data: nodeData }">
                <slot
                    name="node"
                    :node="node"
                    :data="nodeData"
                >
                    {{ nodeData[labelKey] || nodeData.label }}
                </slot>
            </template>
        </el-tree>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Tree as ElTree } from 'element-ui'

/* Types */
export interface TreeNode {
    [key: string]: unknown
    children?: TreeNode[]
}

export interface TreeProps {
    children?: string
    label?: string
    disabled?: string
    isLeaf?: string
}

interface IProps {
    data?: TreeNode[]
    treeProps?: TreeProps
    nodeKey?: string | number
    labelKey?: string
    highlightCurrent?: boolean
    showCheckbox?: boolean
    checkStrictly?: boolean
    accordion?: boolean
    lazy?: boolean
    load?: (node: unknown, resolve: (data: unknown[]) => void) => void
    draggable?: boolean
    defaultCheckedKeys?: (string | number)[]
    defaultExpandedKeys?: (string | number)[]
    currentNodeKey?: string | number | null
    indent?: number
    iconClass?: string
    emptyText?: string
}

/* Props */
const props = withDefaults(defineProps<IProps>(), {
    data: () => [],
    treeProps: () => ({
        children: 'children',
        label: 'label',
        disabled: 'disabled',
    }),
    nodeKey: 'id',
    labelKey: 'label',
    highlightCurrent: false,
    showCheckbox: false,
    checkStrictly: false,
    accordion: false,
    lazy: false,
    draggable: false,
    emptyText: 'No Data',
    indent: 18,
    defaultCheckedKeys: () => [],
    defaultExpandedKeys: () => [],
    iconClass: '',
    currentNodeKey: null
})

/* Emits */
const emit = defineEmits<{
    (e: 'node-click', data: unknown, node: unknown, component: unknown): void
    (e: 'check-change', data: unknown, checked: boolean, indeterminate: boolean): void
    (e: 'current-change', data: unknown, node: unknown): void
    (e: 'check', data: unknown, treeCheckedState: { checkedNodes: unknown[], checkedKeys: (string | number)[], halfCheckedNodes: unknown[], halfCheckedKeys: (string | number)[] }): void
    (e: 'node-expand', data: unknown, node: unknown): void
    (e: 'node-collapse', data: unknown, node: unknown): void
}>()

/* Refs */
const elTreeRef = ref<InstanceType<typeof ElTree>>()

/* Computed */
const treeProps = computed<TreeProps>(() => ({
    ...props.treeProps,
    label: props.labelKey,
}))

/* Event Handlers */
function handleNodeClick (data: unknown, node: unknown, component: unknown) {
    emit('node-click', data, node, component)
}
function handleCheckChange (data: unknown, checked: boolean, indeterminate: boolean) {
    emit('check-change', data, checked, indeterminate)
}
function handleCurrentChange (data: unknown, node: unknown) {
    emit('current-change', data, node)
}
function handleCheck (data: unknown, treeCheckedState: { checkedNodes: unknown[], checkedKeys: (string | number)[], halfCheckedNodes: unknown[], halfCheckedKeys: (string | number)[] }) {
    emit('check', data, treeCheckedState)
}
function handleNodeExpand (data: unknown, node: unknown) {
    emit('node-expand', data, node)
}
function handleNodeCollapse (data: unknown, node: unknown) {
    emit('node-collapse', data, node)
}
/* Public Methods - exposed for parent components */
function setCheckedKeys (keys: (string | number)[], leafOnly = false) {
    elTreeRef.value?.setCheckedKeys(keys, leafOnly)
}
function getCheckedKeys (leafOnly = false) {
    return elTreeRef.value?.getCheckedKeys(leafOnly) ?? []
}
function getCheckedNodes (leafOnly = false, includeHalfChecked = false): unknown[] {
    return elTreeRef.value?.getCheckedNodes(leafOnly, includeHalfChecked) ?? []
}
function setChecked (keyOrData: string | number | unknown, checked: boolean, deep = false) {
    elTreeRef.value?.setChecked(keyOrData, checked, deep)
}

function getHalfCheckedKeys () {
    return elTreeRef.value?.getHalfCheckedKeys() ?? []
}

function getNode (keyOrData: string | number | unknown) {
    return elTreeRef.value?.getNode(keyOrData)
}

function filter (value: string) {
    elTreeRef.value?.filter(value)
}
function expandAll () {
    const nodes = elTreeRef.value?.store?.nodesMap
    if (nodes) {
        Object.values(nodes).forEach((node: unknown) => {
            (node as { expanded: boolean }).expanded = true
        })
    }
}
function collapseAll () {
    const nodes = elTreeRef.value?.store?.nodesMap
    if (nodes) {
        Object.values(nodes).forEach((node: unknown) => {
            (node as { expanded: boolean }).expanded = false
        })
    }
}

/* Expose public API */
defineExpose({
    setCheckedKeys,
    getCheckedKeys,
    getCheckedNodes,
    setChecked,
    getHalfCheckedKeys,
    getNode,
    filter,
    expandAll,
    collapseAll,
    elTreeRef,
})
</script>

<style lang="scss">
.base-tree {
  .tree-label {
    @apply text-gray-950 text-sm mb-2 font-semibold;
  }

  .el-tree-node__content:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }
  .el-tree-node.is-current > .el-tree-node__content {
    background-color: rgba(0, 0, 0, 0.05);
  }
  .el-tree {
    background-color: var(--mainBgColor-color);
    color: var(--mainTextColor-color);
  }
  .el-tree-node__content:hover,
  .el-tree-node:focus > .el-tree-node__content,
  .el-tree.el-tree--highlight-current .el-tree-node.is-current > .el-tree-node__content {
    background-color: color-mix(in srgb, var(--mainTextColor-color) 8%, transparent) !important;
  }
}

/* RTL */
$tree-indent: 18px;
$max-depth: 10;

[dir='rtl'], .rtl {
  .base-tree {
    .el-tree-node__expand-icon {
      transform: rotate(180deg);
    }

    /* Reset padding-left and apply padding-right based on nesting level */
    .el-tree-node__content {
      padding-left: 0 !important;
    }

    @for $i from 0 through $max-depth {
      $selector: '';
      @if $i > 0 {
        @for $j from 1 through $i {
          $selector: $selector + '.el-tree-node ';
        }
      }
      #{$selector}> .el-tree-node__content {
        padding-right: $tree-indent * $i;
      }
    }
  }
}
</style>
